import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import PaginationControls from './PaginationControls';

const AssignUser = () => {
  const { id: userId } = useParams();
  const [trainings, setTraining] = useState([]); // State to hold training details
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [user, setUser] = useState([]);
  const [selectedTrainings, setSelectedTrainings] = useState([]);
  const [selectedTrainingCodes, setSelectedTrainingCodes] = useState({});
  const [error, setError] = useState('');
  const [trainingPage, setTrainingPage] = useState(1);
  const [trainingTotalPages, setTrainingTotalPages] = useState(1);
  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetch(`${API_BASE_URL}/users/get_users.php?id=${userId}`,{
      credentials: 'include'  // This ensures cookies (including session cookies) are sent
    })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        //if (data.success) {
          setUser(data); // Set training details in state
        //}
      })
      .catch(error => console.error('Error fetching user:', error));
  }, [userId]);
 
  useEffect(() => {
    // Fetch trainings under the admin's control (already paginated server-side)
    fetch(`${API_BASE_URL}/trainings/get_all_trainings.php?filter=upcoming&page=${trainingPage}`, { credentials: 'include' })
    .then(response => {
      const contentType = response.headers.get('content-type');
  
      // Log the status and response text for debugging
      console.log('Response Status:', response.status);
      
      // Attempt to read the response as text first
      return response.text().then((text) => {
        console.log('Raw Response:', text); // Log the raw response text
        
        if (!response.ok) {
          // If response is not ok, throw an error with the response text
          throw new Error(`Failed with status ${response.status}: ${text}`);
        }
  
        // If response is JSON, parse it
        if (contentType && contentType.includes('application/json')) {
          return JSON.parse(text); // Parse JSON manually
        } else {
          // Return the raw text if it's not JSON
          return text;
        }
      });
    })
      .then(data => {
        if (Array.isArray(data.trainings)) {
          setTraining(data.trainings.filter(training => training.status !== 'Canceled' && training.status !== 'Postponed'));
          setTrainingTotalPages(data.total_pages || 1);
        } else {
          setTraining([]);
        }
      })
      .catch(err => setError('Failed to fetch trainings'));
  }, [trainingPage]);

  const handleCheckboxChange = (trainingId) => {
    setSelectedTrainings((prev) => {
      if (prev.includes(trainingId)) {
        return prev.filter(id => id !== trainingId); // Remove from selection
      } else {
        return [...prev, trainingId]; // Add to selection
      }
    });
    // Remember the code at selection time, since the training may not be on
    // the currently-loaded page anymore by the time we need it (e.g. for the
    // missing-prerequisites message after selecting across multiple pages).
    const training = trainings.find(t => t.id === trainingId);
    if (training) {
      setSelectedTrainingCodes(prev => ({ ...prev, [trainingId]: training.code }));
    }
  };

  const handleAssign = (bypassPrerequisites = false) => {
    if (selectedTrainings.length === 0) {
      Swal.fire({ icon: 'warning', text: 'Please select at least one training.' });
      return;
    }

    fetch(`${API_BASE_URL}/trainings/assign_user.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
        user_id: userId,
        training_ids: selectedTrainings,
        bypassPrerequisites,
      }),
      credentials: 'include', // Include cookies for session
    })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        console.log("data",data);
        // Check if the response is JSON or plain text
        if (typeof data === 'string') {
          console.log('Non-JSON response:', data);
          Swal.fire({ icon: 'error', text: `Received non-JSON response: ${data}` });
        } else if (data.success) {
          //navigate('/users');
          Swal.fire({ icon: 'success', text: 'Employees successfully assigned' });
          setSelectedTrainings([]);
          setSelectedTrainingCodes({});
        } else if (data.blockedByPrerequisites && data.blockedByPrerequisites.length > 0) {
          const listHtml = data.blockedByPrerequisites.map(b => {
            const code = selectedTrainingCodes[b.id] || trainings.find(t => t.id === b.id)?.code;
            return `<li><strong>${code || 'Training #' + b.id}</strong>: missing ${b.missing.join(', ')}</li>`;
          }).join('');
          Swal.fire({
            title: 'Missing prerequisites',
            html: `<p>This employee has not completed the required prerequisite course(s) for:</p><ul style="text-align:left">${listHtml}</ul>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Assign anyway',
            cancelButtonText: 'Cancel',
          }).then(result => {
            if (result.isConfirmed) {
              handleAssign(true);
            }
          });
        } else if (data.blockedByCapacity && data.blockedByCapacity.length > 0) {
          // Unlike prerequisites, capacity has no bypass — the training is
          // physically full, so this is informational only.
          const listHtml = data.blockedByCapacity.map(b => {
            const code = selectedTrainingCodes[b.id] || trainings.find(t => t.id === b.id)?.code;
            return `<li>${code || 'Training #' + b.id}</li>`;
          }).join('');
          Swal.fire({
            title: 'Training is full',
            html: `<p>This employee could not be assigned to the following — no seats available:</p><ul style="text-align:left">${listHtml}</ul>`,
            icon: 'error',
          });
        } else {
          console.error('PHP Error:', data.error); // Log PHP error to the console
          Swal.fire({ icon: 'error', text: `Failed to assign employees: ${data.error}` });
        }
      })
      .catch(error => {
        console.error('Error assigning training:', error);
        Swal.fire({ icon: 'error', text: 'Error: ' + error.message });
      });
  };

  return (
    <div className='p-2'>
      <h2>Assign An Employee to Training</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {/* Display training information */}
      {user && (
        <div>
          <h3>User Details</h3>
          <p><strong>Name:</strong> {user.name}<br/>
          <strong>Designation:</strong> {user.designation}<br/>
          <strong>Department:</strong> {user.department}<br/>
          <strong>Company:</strong> {user.company}</p>
        </div>
      )}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            <th>Code</th>
            <th>Select</th>
            <th>Title</th>
            <th>Description</th>
            <th>Date</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          {trainings.map((training, index) => (
            <tr key={training.id}>
              <td>{index + 1}</td>
              <td>{training.code}</td>
              <td className='pl-4 text-center'>
                <Form.Check
                  type="checkbox"
                  onChange={() => handleCheckboxChange(training.id)}
                />
              </td>
              <td>{training.title}</td>
              <td>{training.description}</td>
              <td>{training.start_date} - {training.end_date}</td>
              <td>{training.location}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <PaginationControls page={trainingPage} totalPages={trainingTotalPages} onPageChange={setTrainingPage} />
      <Button variant="primary" className="mt-3" onClick={() => handleAssign()}>Assign Selected Training ({selectedTrainings.length})</Button>
    </div>
  );
};

export default AssignUser;
